/**
 * InteractiveQuizPlayer — Phase 12 (June 2026) unified quiz runtime.
 *
 * One component covers all three quiz_format variants of an
 * interactive_quiz scene. The runtime story is intentionally thin
 * because every primitive already exists upstream:
 *
 *   simple_qna       — non-spatial. SessionPage's existing
 *                      QuestionPanel branch handles the MCQ. This
 *                      player just emits telemetry and chains the
 *                      teaching video.
 *
 *   static_spotlight — spatial cycle (legacy DFQ flow). Mounts
 *                      ThreeGame on the shared 3D renderer; the
 *                      session loop fires per-spotlight MCQ
 *                      triggers via the existing TriggerDefinition
 *                      pipeline, exactly as DFQ scenes do today.
 *
 *   animated_board   — spatial reveal (Phase 12 new). Mounts
 *                      ThreeGame; defender route polylines live
 *                      on each actor's initial_state.metadata.route
 *                      and the renderer plays them between snap and
 *                      answer events. The terminal `answer` event's
 *                      multiple_choice trigger fires the scene MCQ.
 *
 * What this component owns:
 *   1. Mounting the right inner renderer for the format.
 *   2. ``interactive_quiz_attempt`` telemetry emission (one event per
 *      learner answer; pivots cleanly on quiz_format / quiz_subject).
 *   3. Auto-playing the bound teaching video on answer reveal so the
 *      coach's "explain after the answer" pedagogy works without
 *      additional clicks.
 *
 * What this component does NOT own:
 *   - The MCQ UI itself (still QuestionPanel, kept central so any quiz
 *     looks the same to the learner regardless of format).
 *   - Mastery scoring (still SessionService → AssessmentEngine).
 *   - Spatial rendering primitives (still ThreeGame / Phaser).
 */

import React, { useEffect, useRef, useMemo } from 'react';
import ThreeGame from '../../game/ThreeGame';
import EventBus from '../../game/EventBus';

// EventBus channel the page-level telemetry transport listens on.
// Keeping the producer (this component) decoupled from the transport
// (PlatformLogger / Kafka producer) lets us swap the sink without
// touching renderer code.
export const QUIZ_ATTEMPT_EVENT = 'interactive_quiz_attempt';

// Channel SessionPage listens on to chain the teaching video into
// the feedback panel. Carries the same signature as a video scene
// pointer so the existing video player can mount unchanged.
export const QUIZ_TEACHING_VIDEO_EVENT = 'interactive_quiz:teaching_video';

const SPATIAL_FORMATS = new Set(['static_spotlight', 'animated_board']);


/**
 * Resolve the scene's quiz_format off either the manifest or its
 * domain_metadata. The session API returns scene metadata at the top
 * of the scene record AND a flattened sceneManifest; we accept either
 * surface so callers don't have to thread one specific path through.
 */
export function resolveQuizFormat(sceneOrManifest) {
  if (!sceneOrManifest) return null;
  return (
    sceneOrManifest.quiz_format
    || sceneOrManifest?.domain_metadata?.quiz_format
    || null
  );
}


/**
 * Public hook: subscribes to the answer flow and:
 *   1. Emits an ``interactive_quiz_attempt`` event for every answered
 *      MCQ inside an interactive_quiz scene.
 *   2. Emits ``interactive_quiz:teaching_video`` so SessionPage can
 *      chain the bound video onto the feedback view.
 *
 * Mount once per session (or per scene); idempotent if mounted twice
 * (events are deduplicated by attemptId at the transport layer).
 */
export function useInteractiveQuizRuntime({ scene, feedback }) {
  const lastAttemptIdRef = useRef(null);
  const promptStartRef = useRef(null);

  // Reset the answer-time clock whenever a new scene mounts. The
  // animated_board flow needs answer-time relative to the trigger
  // firing, not to scene mount; SessionPage tracks the trigger time
  // on its own and overrides this when emitting (the value here is
  // a useful coarse fallback).
  useEffect(() => {
    promptStartRef.current = Date.now();
    return () => { promptStartRef.current = null; };
  }, [scene?.scene_id]);

  // Telemetry + teaching-video chaining fire when feedback lands.
  useEffect(() => {
    if (!feedback || !scene) return;
    const sceneType = scene.scene_type || scene.sceneType;
    if (sceneType !== 'interactive_quiz') return;

    const meta = scene.domain_metadata || scene.domainMetadata || {};
    const attemptId = feedback.attempt_id || feedback.id || `${scene.scene_id}:${Date.now()}`;
    if (lastAttemptIdRef.current === attemptId) return;
    lastAttemptIdRef.current = attemptId;

    const elapsed = promptStartRef.current
      ? Math.max(0, Date.now() - promptStartRef.current)
      : null;

    EventBus.emit(QUIZ_ATTEMPT_EVENT, {
      scene_id:             scene.scene_id,
      domain_id:            scene.domain_id,
      quiz_format:          meta.quiz_format || 'simple_qna',
      quiz_subject:         meta.quiz_subject || 'free',
      time_to_answer_ms:    elapsed,
      was_correct:          !!feedback.is_correct,
      chosen_id:            feedback.choice_id || feedback.chosen_id || null,
      correct_id:           feedback.correct_choice_id || feedback.correct_id || null,
      movement_duration_ms: meta?.movement?.duration_ms || null,
    });

    if (meta.teaching_video_id) {
      EventBus.emit(QUIZ_TEACHING_VIDEO_EVENT, {
        scene_id:    scene.scene_id,
        video_id:    meta.teaching_video_id,
        was_correct: !!feedback.is_correct,
      });
    }
  }, [feedback, scene]);
}


/**
 * Inner renderer: the only branching that lives here is "do I need a
 * 3D canvas?". Non-spatial formats render the page-level QuestionPanel
 * (handled in SessionPage); this player is then a no-op surface.
 */
function InteractiveQuizPlayer({
  sceneManifest,
  rendererConfig,
  actors,
  frame,
  scene,
  feedback,
  orientationOverride,
  children,
}) {
  useInteractiveQuizRuntime({ scene, feedback });

  const fmt = useMemo(
    () => resolveQuizFormat(scene) || resolveQuizFormat(sceneManifest),
    [scene, sceneManifest],
  );

  if (!SPATIAL_FORMATS.has(fmt)) {
    // simple_qna or unknown — let SessionPage drive the QuestionPanel.
    return <>{children}</>;
  }

  // Spatial formats reuse the existing 3D renderer. The animated_board
  // route polylines live on actor.initial_state.metadata.route and are
  // consumed by the football_3d scene class; no new renderer needed.
  return (
    <ThreeGame
      manifest={sceneManifest}
      actors={actors}
      frame={frame}
      sceneKey={sceneManifest?.renderer_key || 'football_3d'}
      orientationOverride={orientationOverride}
    />
  );
}

export default InteractiveQuizPlayer;
